'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useId, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { CheckGlyph, PlusGlyph } from '../../../internal/icons.js';
import { SearchInput } from '../../search/search-input/index.js';
const DEFAULT_MESSAGES = {
    searchLabel: 'Search templates',
    searchPlaceholder: 'Search templates',
    categoriesLabel: 'Categories',
    allCategories: 'All',
    blank: 'Blank',
    results: (n) => (n === 1 ? '1 template' : `${n} templates`),
    noResults: (q) => `No templates match “${q}”.`,
    previewLabel: 'Preview',
};
const textOf = (node) => (typeof node === 'string' ? node : '');
/** Whether a template matches a search query (name, string description and keywords, any order). */
export function templateMatches(template, query) {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0)
        return true;
    const hay = [template.name, textOf(template.description), ...(template.keywords ?? [])]
        .join(' ')
        .toLowerCase();
    return words.every((w) => hay.includes(w));
}
/** Columns in the first row of a rendered grid, from the items' positions. */
function columnsOf(items) {
    const first = items[0];
    if (!first)
        return 1;
    let n = 0;
    for (const el of items) {
        if (el.offsetTop !== first.offsetTop)
            break;
        n += 1;
    }
    return Math.max(1, n);
}
/**
 * Choose a template from a gallery: category filters, search, a blank
 * option and an optional preview pane. The cards are a radio group with one
 * Tab stop; arrow keys move through the grid by row and column and select as
 * they go. The product supplies the templates and applies the choice.
 */
export const TemplatePicker = forwardRef(function TemplatePicker({ templates, categories, value: valueProp, defaultValue = null, onValueChange, blank, searchable = true, defaultCategory, renderPreview, minCardWidth = '9rem', messages: messagesProp, className, style, 'aria-label': ariaLabel = 'Templates', 'aria-labelledby': ariaLabelledBy, ...rest }, ref) {
    const messages = { ...DEFAULT_MESSAGES, ...messagesProp };
    const [inner, setInner] = useState(defaultValue);
    const controlled = valueProp !== undefined;
    const value = controlled ? valueProp : inner;
    const [query, setQuery] = useState('');
    const [category, setCategory] = useState(defaultCategory ?? null);
    const gridRef = useRef(null);
    const auto = useId();
    const blankCard = blank
        ? {
            id: (typeof blank === 'object' && blank.id) || 'blank',
            name: (typeof blank === 'object' && blank.name) || messages.blank,
            description: typeof blank === 'object' ? blank.description : undefined,
            preview: (typeof blank === 'object' && blank.preview) || (_jsx("span", { className: "mn-template-picker__blank-mark", children: _jsx(PlusGlyph, {}) })),
        }
        : null;
    const shown = templates.filter((t) => (category === null || t.category === category) && templateMatches(t, query));
    const cards = [
        ...(blankCard ? [{ id: blankCard.id, template: null, disabled: false }] : []),
        ...shown.map((t) => ({ id: t.id, template: t, disabled: Boolean(t.disabled) })),
    ];
    const enabled = cards.filter((c) => !c.disabled);
    const tabStop = enabled.find((c) => c.id === value)?.id ?? enabled[0]?.id;
    const select = (card) => {
        if (card.disabled)
            return;
        if (!controlled)
            setInner(card.id);
        onValueChange?.(card.id, card.template);
    };
    const onKeyDown = (event, index) => {
        const items = Array.from(gridRef.current?.querySelectorAll('.mn-template-picker__card') ?? []);
        const cols = columnsOf(items);
        const rtl = getComputedStyle(event.currentTarget).direction === 'rtl';
        const step = (from, delta, wrap) => {
            let i = from;
            for (let n = 0; n < cards.length; n += 1) {
                i += delta;
                if (wrap)
                    i = (i + cards.length) % cards.length;
                if (i < 0 || i >= cards.length)
                    return null;
                if (!cards[i]?.disabled)
                    return i;
            }
            return null;
        };
        let next = null;
        switch (event.key) {
            case 'ArrowRight':
                next = step(index, rtl ? -1 : 1, true);
                break;
            case 'ArrowLeft':
                next = step(index, rtl ? 1 : -1, true);
                break;
            case 'ArrowDown':
                next = step(index, cols, false);
                break;
            case 'ArrowUp':
                next = step(index, -cols, false);
                break;
            case 'Home':
                next = step(-1, 1, false);
                break;
            case 'End':
                next = step(cards.length, -1, false);
                break;
            default:
                return;
        }
        event.preventDefault();
        if (next === null)
            return;
        const card = cards[next];
        select(card);
        items[next]?.focus();
    };
    const selected = value === null
        ? undefined
        : blankCard && value === blankCard.id
            ? null
            : templates.find((t) => t.id === value);
    return (_jsxs("div", { ref: ref, ...rest, className: cx('mn-template-picker', className), "data-has-preview": renderPreview ? '' : undefined, style: { ...style, ['--mn-template-picker-min']: minCardWidth }, children: [searchable || categories?.length ? (_jsxs("div", { className: "mn-template-picker__filters", children: [searchable ? (_jsx(SearchInput, { size: "sm", "aria-label": messages.searchLabel, placeholder: messages.searchPlaceholder, value: query, onValueChange: setQuery, className: "mn-template-picker__search" })) : null, categories?.length ? (_jsx("div", { role: "group", "aria-label": messages.categoriesLabel, className: "mn-template-picker__categories", children: [{ id: null, label: messages.allCategories }, ...categories].map((c) => (_jsx("button", { type: "button", className: "mn-template-picker__category", "aria-pressed": category === c.id, onClick: () => setCategory(c.id), children: c.label }, c.id ?? '__all'))) })) : null] })) : null, _jsx("p", { className: "mn-visually-hidden", role: "status", children: query || category !== null ? messages.results(shown.length) : '' }), _jsxs("div", { className: "mn-template-picker__body", children: [_jsx("div", { ref: gridRef, role: "radiogroup", "aria-label": ariaLabelledBy ? undefined : ariaLabel, "aria-labelledby": ariaLabelledBy, className: "mn-template-picker__grid", children: cards.map((card, index) => {
                            const t = card.template;
                            const name = t ? t.name : (blankCard?.name ?? '');
                            const description = t ? t.description : blankCard?.description;
                            const preview = t ? t.preview : blankCard?.preview;
                            const checked = card.id === value;
                            const nameId = `${auto}-${index}-name`;
                            const descId = `${auto}-${index}-desc`;
                            const hasDesc = Boolean(description) || Boolean(t?.badge);
                            return (_jsxs("button", { type: "button", role: "radio", "aria-checked": checked, "aria-labelledby": nameId, "aria-describedby": hasDesc ? descId : undefined, disabled: card.disabled, tabIndex: card.id === tabStop ? 0 : -1, className: "mn-template-picker__card", "data-blank": t ? undefined : '', "data-state": checked ? 'checked' : 'unchecked', onClick: () => select(card), onKeyDown: (e) => onKeyDown(e, index), children: [preview ? (_jsx("span", { className: "mn-template-picker__media", "aria-hidden": true, children: preview })) : null, checked ? (_jsx("span", { className: "mn-template-picker__check", "aria-hidden": true, children: _jsx(CheckGlyph, {}) })) : null, _jsxs("span", { className: "mn-template-picker__text", children: [_jsx("span", { id: nameId, className: "mn-template-picker__name", children: name }), hasDesc ? (_jsxs("span", { id: descId, className: "mn-template-picker__description", children: [t?.badge ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "mn-template-picker__badge", children: t.badge }), ' '] })) : null, description] })) : null] })] }, card.id));
                        }) }), renderPreview && selected !== undefined ? (_jsx("section", { className: "mn-template-picker__preview", "aria-label": messages.previewLabel, children: renderPreview(selected) })) : null] }), shown.length === 0 && (query || category !== null) ? (_jsx("p", { className: "mn-template-picker__empty", children: query ? messages.noResults(query.trim()) : messages.results(0) })) : null] }));
});
//# sourceMappingURL=template-picker.js.map